import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import UserAvatar from "../components/UserAvatar";
import {
    ArrowLeft, Store, Plus, Loader2, AlertCircle, Coins, Package, Zap,
    Clock, Image as ImageIcon, X, Check, ThumbsUp, ThumbsDown, Send,
    Truck, ShoppingBag, Search, Trash2, Shield, Timer,
} from "lucide-react";

const FEE_PCT = 3;
const MAX_IMAGES = 4;
const MAX_IMG_BYTES = 8 * 1024 * 1024;

function fmtRel(iso) {
    if (!iso) return "";
    const d = new Date(iso).getTime();
    const diff = Date.now() - d;
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const dd = Math.floor(h / 24);
    return `${dd}d ago`;
}

function fmtCountdown(deadlineIso) {
    if (!deadlineIso) return "";
    const left = new Date(deadlineIso).getTime() - Date.now();
    if (left <= 0) return "expired";
    const h = Math.floor(left / 3600000);
    const m = Math.floor((left % 3600000) / 60000);
    if (h > 0) return `${h}h ${m}m left`;
    const s = Math.floor((left % 60000) / 1000);
    return `${m}m ${s}s left`;
}

function statusBadge(status) {
    const map = {
        ACTIVE: ["bg-emerald-600 text-white", "For sale"],
        SOLD: ["bg-vibrantyellow text-ink", "In escrow"],
        COMPLETED: ["bg-vibrantblue text-white", "Completed"],
        REFUNDED: ["bg-ink text-white", "Refunded"],
        DISPUTED: ["bg-vibrantpink text-white", "Disputed"],
        CANCELLED: ["bg-ink text-white/70", "Cancelled"],
    };
    const [cls, label] = map[status] || ["bg-cream text-ink", status];
    return <span className={`font-heading font-black text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${cls}`}>{label}</span>;
}

function CategoryBadge({ category }) {
    const digital = category === "digital";
    return (
        <span className={`inline-flex items-center gap-1 font-heading font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border-[2px] ${digital ? "bg-vibrantblue/15 border-vibrantblue text-vibrantblue" : "bg-vibrantorange/15 border-vibrantorange text-vibrantorange"}`}>
            {digital ? <Zap className="w-3 h-3" strokeWidth={2.5} /> : <Package className="w-3 h-3" strokeWidth={2.5} />}
            {digital ? "Digital" : "Physical"}
        </span>
    );
}

function DeliveryBadge({ mode, hours }) {
    if (mode === "instant") {
        return <span className="inline-flex items-center gap-1 font-heading font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-600 text-white"><Zap className="w-3 h-3" strokeWidth={2.5} /> Instant</span>;
    }
    return <span className="inline-flex items-center gap-1 font-heading font-bold text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-vibrantyellow text-ink"><Shield className="w-3 h-3" strokeWidth={2.5} /> Escrow {hours}h</span>;
}

function ListingImage({ listing, idx = 0, className = "" }) {
    const n = Number(listing && listing.image_count) || 0;
    if (!n) {
        return (
            <div className={`${className} bg-cream border-[3px] border-ink rounded-xl flex items-center justify-center`}>
                <ImageIcon className="w-10 h-10 text-ink/30" strokeWidth={2} />
            </div>
        );
    }
    return (
        <img
            src={`${API}/marketplace/listings/${listing.id}/images/${idx}`}
            alt={listing.title}
            className={`${className} object-cover border-[3px] border-ink rounded-xl bg-cream`}
            loading="lazy"
        />
    );
}

function CreateForm({ onCreated, onCancel }) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("digital");
    const [price, setPrice] = useState(10);
    const [deliveryMode, setDeliveryMode] = useState("instant");
    const [escrowHours, setEscrowHours] = useState(24);
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const previews = useMemo(() => files.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })), [files]);
    useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

    const onFiles = (e) => {
        const picked = Array.from(e.target.files || []).slice(0, MAX_IMAGES);
        for (const f of picked) {
            if (f.size > MAX_IMG_BYTES) {
                setErr(`${f.name} is bigger than 8MB`);
                return;
            }
        }
        setErr("");
        setFiles(picked);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!title.trim() || !description.trim() || !price) {
            setErr("Title, description and price are required");
            return;
        }
        setErr("");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("title", title.trim());
            fd.append("description", description.trim());
            fd.append("category", category);
            fd.append("price", String(parseInt(price) || 0));
            fd.append("delivery_mode", deliveryMode);
            if (deliveryMode === "escrow") fd.append("escrow_hours", String(parseInt(escrowHours) || 24));
            for (const f of files) fd.append("files", f);
            const { data } = await api.post("/marketplace/listings", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onCreated(data.listing);
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setBusy(false);
        }
    };

    const feeCredits = Math.round((parseInt(price) || 0) * FEE_PCT / 100);
    const payout = (parseInt(price) || 0) - feeCredits;

    return (
        <form onSubmit={submit} data-testid="marketplace-create-form" className="brutal-card p-5 bg-white space-y-3">
            <div className="flex items-center gap-2 mb-1">
                <Plus className="w-5 h-5 text-vibrantorange" strokeWidth={2.5} />
                <h3 className="font-heading font-black text-xl tracking-tighter text-ink">List something for sale</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block">
                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Title</span>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value.slice(0, 120))}
                        placeholder="Vintage t-shirt, Lightroom preset pack, 1-hour consult…"
                        data-testid="marketplace-title-input"
                        className="w-full mt-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 font-heading font-bold text-base text-ink focus:outline-none"
                    />
                </label>
                <label className="block">
                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Price (credits)</span>
                    <input
                        type="number"
                        min={1}
                        max={1000000}
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        data-testid="marketplace-price-input"
                        className="w-full mt-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 font-heading font-black text-base text-ink focus:outline-none"
                    />
                </label>
            </div>
            <label className="block">
                <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Description</span>
                <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value.slice(0, 4000))}
                    placeholder="What is it? What does the buyer get? Condition, shipping zones, any disclaimers."
                    data-testid="marketplace-description-input"
                    className="w-full mt-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 font-body text-sm text-ink focus:outline-none"
                />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Type</span>
                    <div className="mt-1 flex gap-2">
                        <button type="button" onClick={() => setCategory("digital")} data-testid="marketplace-type-digital" className={`flex-1 brutal-btn text-sm py-2 ${category === "digital" ? "bg-vibrantblue text-white" : "bg-cream text-ink"}`}>
                            <Zap className="w-4 h-4 mr-1" strokeWidth={2.5} /> Digital
                        </button>
                        <button type="button" onClick={() => setCategory("physical")} data-testid="marketplace-type-physical" className={`flex-1 brutal-btn text-sm py-2 ${category === "physical" ? "bg-vibrantorange text-white" : "bg-cream text-ink"}`}>
                            <Package className="w-4 h-4 mr-1" strokeWidth={2.5} /> Physical
                        </button>
                    </div>
                </div>
                <div>
                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Delivery</span>
                    <div className="mt-1 flex gap-2">
                        <button type="button" onClick={() => setDeliveryMode("instant")} data-testid="marketplace-delivery-instant" className={`flex-1 brutal-btn text-sm py-2 ${deliveryMode === "instant" ? "bg-emerald-600 text-white" : "bg-cream text-ink"}`}>
                            Instant release
                        </button>
                        <button type="button" onClick={() => setDeliveryMode("escrow")} data-testid="marketplace-delivery-escrow" className={`flex-1 brutal-btn text-sm py-2 ${deliveryMode === "escrow" ? "bg-vibrantyellow text-ink" : "bg-cream text-ink"}`}>
                            Escrow
                        </button>
                    </div>
                </div>
            </div>
            {deliveryMode === "escrow" && (
                <label className="block">
                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Escrow window (hours) — buyer can confirm receipt anytime; after this timer either party can vote</span>
                    <input
                        type="number"
                        min={1}
                        max={720}
                        value={escrowHours}
                        onChange={(e) => setEscrowHours(e.target.value)}
                        data-testid="marketplace-escrow-hours-input"
                        className="w-full mt-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 font-heading font-black text-base text-ink focus:outline-none"
                    />
                </label>
            )}
            <label className="block">
                <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Photos (optional · up to {MAX_IMAGES})</span>
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={onFiles}
                    data-testid="marketplace-images-input"
                    className="block mt-1 w-full text-sm text-ink file:mr-3 file:brutal-btn file:bg-vibrantpink file:text-white file:text-xs file:py-2 file:px-3 file:border-none"
                />
                {previews.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                        {previews.map((p, i) => (
                            <div key={i} className="w-16 h-16 border-[3px] border-ink rounded-lg overflow-hidden bg-cream">
                                <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                            </div>
                        ))}
                    </div>
                )}
            </label>

            <div className="rounded-xl bg-cream border-[3px] border-ink/15 p-3 text-xs font-body text-ink/80">
                You'll receive <span className="font-heading font-black text-ink">{Math.max(0, payout)}c</span> after the platform's {FEE_PCT}% fee ({feeCredits}c) on each sale.
                {deliveryMode === "escrow" && (
                    <> Funds stay locked until buyer confirms receipt, you both vote matching, or you resolve a dispute.</>
                )}
            </div>

            {err && (
                <div className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                    <span className="font-body font-semibold text-xs" data-testid="marketplace-create-error">{err}</span>
                </div>
            )}

            <div className="flex gap-2 justify-end pt-1">
                {onCancel && (
                    <button type="button" onClick={onCancel} className="brutal-btn-white text-sm py-2 px-4" data-testid="marketplace-create-cancel">
                        Cancel
                    </button>
                )}
                <button type="submit" disabled={busy} className="brutal-btn-primary text-sm py-2 px-4 disabled:opacity-50" data-testid="marketplace-create-submit">
                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2.5} /> : <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} />}
                    Publish listing
                </button>
            </div>
        </form>
    );
}

function BuyPanel({ listing, me, onDone }) {
    const [address, setAddress] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const buy = async () => {
        setErr("");
        setBusy(true);
        try {
            const body = {};
            if (listing.category === "physical") body.shipping_address = address.trim();
            const { data } = await api.post(`/marketplace/listings/${listing.id}/buy`, body);
            onDone(data.listing);
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setBusy(false);
        }
    };

    if (!me || me === false) {
        return (
            <div className="brutal-card p-4 bg-cream">
                <p className="font-heading font-bold text-sm text-ink/60"><Link to="/login" className="text-vibrantorange underline">Log in</Link> to buy this item.</p>
            </div>
        );
    }
    if (me.id === listing.seller_id) {
        return (
            <div className="brutal-card p-4 bg-cream">
                <p className="font-heading font-bold text-sm text-ink/60">This is your own listing.</p>
            </div>
        );
    }

    return (
        <div className="brutal-card p-4 bg-white" data-testid="marketplace-buy-panel">
            {listing.category === "physical" && (
                <label className="block mb-3">
                    <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">Shipping address</span>
                    <textarea
                        rows={3}
                        value={address}
                        onChange={(e) => setAddress(e.target.value.slice(0, 600))}
                        placeholder="Name, street, city, state, postal code, country"
                        data-testid="marketplace-shipping-input"
                        className="w-full mt-1 bg-cream border-[3px] border-ink rounded-xl px-3 py-2 font-body text-sm text-ink focus:outline-none"
                    />
                </label>
            )}
            {listing.delivery_mode === "escrow" ? (
                <p className="font-body text-xs text-ink/70 mb-3">
                    Payment is <span className="font-heading font-bold">held in escrow</span> for {listing.escrow_hours}h. Confirm when you receive the item, or wait and vote with the seller after the window.
                </p>
            ) : (
                <p className="font-body text-xs text-ink/70 mb-3">
                    This is an <span className="font-heading font-bold">instant-release</span> listing. Credits transfer to the seller immediately after purchase. No refund window.
                </p>
            )}
            {err && (
                <div className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-2 mb-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                    <span className="font-body font-semibold text-xs" data-testid="marketplace-buy-error">{err}</span>
                </div>
            )}
            <button
                type="button"
                onClick={buy}
                disabled={busy || (listing.category === "physical" && !address.trim())}
                data-testid="marketplace-buy-btn"
                className="brutal-btn-primary w-full text-sm py-2.5 disabled:opacity-50"
            >
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2.5} /> : <ShoppingBag className="w-4 h-4 mr-1.5" strokeWidth={2.5} />}
                Buy for {listing.price}c
            </button>
        </div>
    );
}

function OrderPanel({ listing, me, onUpdate }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState("");
    const [err, setErr] = useState("");

    const amSeller = me && me.id === listing.seller_id;
    const amBuyer = me && me.id === listing.buyer_id;
    if (!amSeller && !amBuyer) return null;
    if (!["SOLD", "DISPUTED", "COMPLETED", "REFUNDED"].includes(listing.status)) return null;

    const call = async (verb, body) => {
        setErr("");
        setBusy(verb);
        try {
            const { data } = await api.post(`/marketplace/listings/${listing.id}/${verb}`, body || {});
            onUpdate(data.listing);
            if (verb === "deliver") setNote("");
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setBusy("");
        }
    };

    const past = listing.escrow_deadline && (new Date(listing.escrow_deadline).getTime() <= Date.now());
    const canConfirm = amBuyer && listing.status === "SOLD";
    const canVote = (amBuyer || amSeller) && (listing.status === "SOLD" || listing.status === "DISPUTED");
    const myVote = amSeller ? listing.seller_vote : listing.buyer_vote;

    return (
        <div className="brutal-card p-4 bg-cream space-y-3" data-testid="marketplace-order-panel">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="font-heading font-black text-base text-ink tracking-tighter">
                    {listing.status === "COMPLETED" ? "Order complete" : listing.status === "REFUNDED" ? "Order refunded" : listing.status === "DISPUTED" ? "Order disputed" : amSeller ? "Fulfil this order" : "Your purchase"}
                </div>
                {listing.escrow_deadline && listing.status === "SOLD" && (
                    <span className="flex items-center gap-1 font-heading font-bold text-xs text-ink/70 tabular-nums" data-testid="marketplace-escrow-countdown">
                        <Timer className="w-3.5 h-3.5" strokeWidth={2.5} /> {fmtCountdown(listing.escrow_deadline)}
                    </span>
                )}
            </div>

            {listing.category === "physical" && listing.shipping_address && (
                <div className="rounded-xl bg-white border-[3px] border-ink/15 p-2 text-xs">
                    <div className="font-heading font-bold uppercase tracking-widest text-ink/50 text-[10px] mb-0.5">Ship to</div>
                    <div className="font-body text-ink whitespace-pre-wrap" data-testid="marketplace-shipping-address">{listing.shipping_address}</div>
                </div>
            )}

            {listing.delivery_content && (
                <div className="rounded-xl bg-white border-[3px] border-ink/15 p-2 text-xs">
                    <div className="font-heading font-bold uppercase tracking-widest text-ink/50 text-[10px] mb-0.5">
                        {listing.category === "digital" ? "Digital delivery" : "Seller's note / tracking"}
                    </div>
                    <div className="font-body text-ink whitespace-pre-wrap break-words" data-testid="marketplace-delivery-content">{listing.delivery_content}</div>
                </div>
            )}

            {amSeller && listing.status === "SOLD" && (
                <div>
                    <label className="block">
                        <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/60">
                            {listing.category === "digital" ? "Deliver digital content / link / code" : "Tracking info / shipping note"}
                        </span>
                        <textarea
                            rows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value.slice(0, 4000))}
                            placeholder={listing.category === "digital" ? "Download link, access code, file contents, etc." : "Carrier + tracking number, dispatch note."}
                            data-testid="marketplace-deliver-input"
                            className="w-full mt-1 bg-white border-[3px] border-ink rounded-xl px-3 py-2 font-body text-sm text-ink focus:outline-none"
                        />
                    </label>
                    <button
                        type="button"
                        onClick={() => call("deliver", { content: note })}
                        disabled={!note.trim() || busy === "deliver"}
                        data-testid="marketplace-deliver-btn"
                        className="brutal-btn-primary mt-2 text-sm py-2 px-4 disabled:opacity-50"
                    >
                        {busy === "deliver" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2.5} /> : <Send className="w-4 h-4 mr-1.5" strokeWidth={2.5} />}
                        {listing.delivery_content ? "Update delivery" : "Mark delivered"}
                    </button>
                </div>
            )}

            {canConfirm && (
                <button
                    type="button"
                    onClick={() => call("confirm")}
                    disabled={busy === "confirm"}
                    data-testid="marketplace-confirm-btn"
                    className="brutal-btn w-full text-sm py-2 bg-emerald-600 text-white disabled:opacity-50"
                >
                    {busy === "confirm" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" strokeWidth={2.5} /> : <Check className="w-4 h-4 mr-1.5" strokeWidth={2.5} />}
                    I received the item — release {listing.price - (listing.fee_credits || 0)}c to seller
                </button>
            )}

            {canVote && (
                <div className="pt-1 border-t-[2px] border-ink/10">
                    <div className="font-heading font-bold text-xs uppercase tracking-widest text-ink/50 mb-1">
                        Vote to resolve {past ? "(timer expired)" : ""}
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => call("vote", { vote: "YES" })}
                            disabled={busy === "vote"}
                            data-testid="marketplace-vote-yes"
                            className={`flex-1 brutal-btn text-xs py-2 ${myVote === "YES" ? "bg-emerald-600 text-white" : "bg-white text-ink"}`}
                        >
                            <ThumbsUp className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Delivered — pay seller
                        </button>
                        <button
                            type="button"
                            onClick={() => call("vote", { vote: "NO" })}
                            disabled={busy === "vote"}
                            data-testid="marketplace-vote-no"
                            className={`flex-1 brutal-btn text-xs py-2 ${myVote === "NO" ? "bg-vibrantpink text-white" : "bg-white text-ink"}`}
                        >
                            <ThumbsDown className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Not delivered — refund buyer
                        </button>
                    </div>
                    <div className="mt-1 flex justify-between text-[11px] font-body text-ink/60">
                        <span>Seller voted: <span className="font-heading font-bold text-ink">{listing.seller_vote || "—"}</span></span>
                        <span>Buyer voted: <span className="font-heading font-bold text-ink">{listing.buyer_vote || "—"}</span></span>
                    </div>
                </div>
            )}

            {err && (
                <div className="flex items-start gap-2 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink rounded-xl p-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                    <span className="font-body font-semibold text-xs" data-testid="marketplace-order-error">{err}</span>
                </div>
            )}
        </div>
    );
}

function ListingDetail({ listing, me, onBack, onUpdate }) {
    const [imgIdx, setImgIdx] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    const amSeller = me && me.id === listing.seller_id;

    const doDelete = async () => {
        setBusy(true);
        try {
            await api.delete(`/marketplace/listings/${listing.id}`);
            onBack(true);
        } catch (ex) {
            setConfirmDelete(false);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="marketplace-detail">
            <button onClick={() => onBack(false)} data-testid="marketplace-detail-back" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
                Back to marketplace
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                <div className="lg:col-span-3 space-y-3">
                    <ListingImage listing={listing} idx={imgIdx} className="w-full aspect-video" />
                    {listing.image_count > 1 && (
                        <div className="flex gap-2 flex-wrap">
                            {Array.from({ length: listing.image_count }).map((_, i) => (
                                <button key={i} type="button" onClick={() => setImgIdx(i)} data-testid={`marketplace-thumb-${i}`} className={`w-16 h-16 border-[3px] rounded-lg overflow-hidden ${i === imgIdx ? "border-vibrantorange" : "border-ink"}`}>
                                    <ListingImage listing={listing} idx={i} className="w-full h-full !border-0 !rounded-none" />
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="brutal-card p-5 bg-white">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                            <CategoryBadge category={listing.category} />
                            <DeliveryBadge mode={listing.delivery_mode} hours={listing.escrow_hours} />
                            {statusBadge(listing.status)}
                        </div>
                        <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-ink mb-1" data-testid="marketplace-detail-title">{listing.title}</h1>
                        <div className="flex items-center gap-2 mb-3">
                            <Link to={`/u/${listing.seller_username}`} className="flex items-center gap-1.5 hover:underline">
                                <UserAvatar username={listing.seller_username} size={22} className="!shadow-none !border-2" />
                                <span className="font-heading font-bold text-sm text-ink/70">@{listing.seller_username}</span>
                            </Link>
                            <span className="text-ink/40">·</span>
                            <span className="font-body text-xs text-ink/50">{fmtRel(listing.created_at)}</span>
                        </div>
                        <p className="font-body text-sm text-ink whitespace-pre-wrap break-words" data-testid="marketplace-detail-description">{listing.description}</p>
                    </div>
                </div>
                <div className="lg:col-span-2 space-y-3">
                    <div className="brutal-card p-4 bg-vibrantyellow">
                        <div className="flex items-center justify-between">
                            <span className="font-heading font-bold text-xs uppercase tracking-widest text-ink/70">Price</span>
                            <span className="font-heading font-black text-3xl text-ink tabular-nums" data-testid="marketplace-detail-price">{listing.price}c</span>
                        </div>
                    </div>
                    {listing.status === "ACTIVE" ? (
                        <BuyPanel listing={listing} me={me} onDone={onUpdate} />
                    ) : (
                        <OrderPanel listing={listing} me={me} onUpdate={onUpdate} />
                    )}
                    {amSeller && listing.status === "ACTIVE" && (
                        confirmDelete ? (
                            <div className="brutal-card p-3 bg-ink text-white flex items-center gap-2">
                                <span className="font-heading font-bold text-sm">Remove this listing?</span>
                                <button onClick={() => setConfirmDelete(false)} className="brutal-btn-white text-xs py-1.5 px-2 ml-auto" data-testid="marketplace-delete-cancel">No</button>
                                <button onClick={doDelete} disabled={busy} data-testid="marketplace-delete-confirm" className="brutal-btn text-xs py-1.5 px-2 bg-vibrantpink text-white disabled:opacity-50">Yes, remove</button>
                            </div>
                        ) : (
                            <button type="button" onClick={() => setConfirmDelete(true)} data-testid="marketplace-delete-btn" className="brutal-btn-white w-full text-xs py-2">
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Remove listing
                            </button>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

function ListingCard({ listing, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(listing)}
            data-testid={`marketplace-card-${listing.id}`}
            className="brutal-card p-3 bg-white text-left hover:-translate-y-0.5 transition-transform w-full min-w-0 flex flex-col overflow-hidden"
        >
            <ListingImage listing={listing} className="w-full aspect-[4/3] mb-2" />
            <div className="flex items-center gap-1.5 flex-wrap mb-1 min-w-0">
                <CategoryBadge category={listing.category} />
                <DeliveryBadge mode={listing.delivery_mode} hours={listing.escrow_hours} />
            </div>
            <h3 className="font-heading font-black text-base text-ink tracking-tighter line-clamp-2 min-h-[2.5rem] break-words">{listing.title}</h3>
            <div className="flex items-end justify-between gap-2 mt-1 min-w-0">
                <div className="flex items-center gap-1 text-xs text-ink/50 font-body min-w-0 flex-1 truncate">
                    <UserAvatar username={listing.seller_username} size={18} className="!shadow-none !border-2 flex-shrink-0" />
                    <span className="truncate">@{listing.seller_username}</span>
                </div>
                <span className="font-heading font-black text-lg text-vibrantorange tabular-nums flex-shrink-0">{listing.price}c</span>
            </div>
            {listing.status !== "ACTIVE" && (
                <div className="mt-1">{statusBadge(listing.status)}</div>
            )}
        </button>
    );
}

export default function Marketplace() {
    const { user } = useAuth();
    const [tab, setTab] = useState("browse");       // browse | mine | purchases
    const [category, setCategory] = useState("all");
    const [sort, setSort] = useState("new");
    const [search, setSearch] = useState("");
    const [showCreate, setShowCreate] = useState(false);
    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [selected, setSelected] = useState(null);

    const fetchList = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            let url;
            if (tab === "mine") url = "/marketplace/my/listings";
            else if (tab === "purchases") url = "/marketplace/my/purchases";
            else {
                const q = new URLSearchParams({ sort });
                if (category !== "all") q.set("category", category);
                if (search.trim()) q.set("search", search.trim());
                url = `/marketplace/listings?${q}`;
            }
            const { data } = await api.get(url);
            setListings(Array.isArray(data) ? data : []);
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setLoading(false);
        }
    }, [tab, category, sort, search]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    // Refresh selected listing from server to keep order panel fresh
    const refreshSelected = useCallback(async (id) => {
        try {
            const { data } = await api.get(`/marketplace/listings/${id}`);
            setSelected(data.listing);
        } catch {}
    }, []);

    useEffect(() => {
        if (!selected) return;
        if (!["SOLD", "DISPUTED"].includes(selected.status)) return;
        const t = setInterval(() => refreshSelected(selected.id), 6000);
        return () => clearInterval(t);
    }, [selected, refreshSelected]);

    if (selected) {
        return (
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
                <ListingDetail
                    listing={selected}
                    me={user && user !== false ? user : null}
                    onBack={(afterDelete) => { setSelected(null); if (afterDelete) fetchList(); }}
                    onUpdate={(l) => { setSelected(l); fetchList(); }}
                />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div>
                    <div className="inline-flex items-center gap-2 mb-1">
                        <div className="w-10 h-10 rounded-xl border-[3px] border-ink bg-vibrantorange shadow-brutal flex items-center justify-center">
                            <Store className="w-5 h-5 text-white" strokeWidth={2.5} />
                        </div>
                        <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-ink">
                            Market<span className="text-vibrantorange">place</span>
                        </h1>
                    </div>
                    <p className="font-body text-sm text-ink/60">Sell digital or physical items in exchange for credits. Platform takes {FEE_PCT}% per sale.</p>
                </div>
                {user && user !== false && (
                    <button
                        type="button"
                        onClick={() => setShowCreate((v) => !v)}
                        data-testid="marketplace-new-listing-btn"
                        className="brutal-btn-primary text-sm py-2.5 px-4"
                    >
                        {showCreate ? <X className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> : <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} />}
                        {showCreate ? "Cancel" : "List an item"}
                    </button>
                )}
            </div>

            {(!user || user === false) && (
                <div className="brutal-card p-3 bg-vibrantyellow mb-4 text-sm font-heading font-bold text-ink">
                    <Link to="/login" className="text-vibrantorange underline" data-testid="marketplace-login-link">Log in</Link> to buy or sell.
                </div>
            )}

            {showCreate && user && user !== false && (
                <div className="mb-5">
                    <CreateForm
                        onCreated={(l) => { setShowCreate(false); setTab("mine"); setListings((cur) => [l, ...cur]); setSelected(l); }}
                        onCancel={() => setShowCreate(false)}
                    />
                </div>
            )}

            <div className="flex items-center gap-2 flex-wrap mb-4">
                {[["browse", "Browse", Store], ["mine", "My listings", Package], ["purchases", "My purchases", ShoppingBag]].map(([k, label, Icon]) => {
                    const needsAuth = k !== "browse";
                    const disabled = needsAuth && (!user || user === false);
                    return (
                        <button
                            key={k}
                            type="button"
                            onClick={() => !disabled && setTab(k)}
                            data-testid={`marketplace-tab-${k}`}
                            disabled={disabled}
                            className={`brutal-btn text-xs py-2 px-3 ${tab === k ? "bg-ink text-white" : "bg-white text-ink"} disabled:opacity-40`}
                        >
                            <Icon className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> {label}
                        </button>
                    );
                })}
            </div>

            {tab === "browse" && (
                <div className="brutal-card p-3 bg-cream mb-4 flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 flex-1 min-w-[180px] bg-white border-[3px] border-ink rounded-xl px-2 py-1.5">
                        <Search className="w-4 h-4 text-ink/50" strokeWidth={2.5} />
                        <input
                            type="text"
                            placeholder="Search listings…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value.slice(0, 120))}
                            data-testid="marketplace-search-input"
                            className="flex-1 bg-transparent font-body text-sm text-ink focus:outline-none"
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        {[["all", "All"], ["digital", "Digital"], ["physical", "Physical"]].map(([k, label]) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => setCategory(k)}
                                data-testid={`marketplace-filter-${k}`}
                                className={`brutal-btn text-xs py-1.5 px-2 ${category === k ? "bg-vibrantorange text-white" : "bg-white text-ink"}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        data-testid="marketplace-sort-select"
                        className="bg-white border-[3px] border-ink rounded-xl px-2 py-1.5 font-heading font-bold text-xs text-ink focus:outline-none"
                    >
                        <option value="new">Newest</option>
                        <option value="price_asc">Price: low → high</option>
                        <option value="price_desc">Price: high → low</option>
                    </select>
                </div>
            )}

            {err && (
                <div className="brutal-card p-3 bg-vibrantpink/15 border-[3px] border-vibrantpink text-ink mb-3 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                    <span className="font-body font-semibold text-xs">{err}</span>
                </div>
            )}

            {loading ? (
                <div className="py-16 text-center">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin text-vibrantorange" strokeWidth={2.5} />
                </div>
            ) : listings.length === 0 ? (
                <div className="brutal-card p-10 bg-cream text-center" data-testid="marketplace-empty">
                    <Store className="w-10 h-10 mx-auto text-ink/30 mb-2" strokeWidth={2} />
                    <p className="font-heading font-bold text-base text-ink/60">
                        {tab === "browse" ? "No listings yet — be the first to sell something." : tab === "mine" ? "You haven't listed anything yet." : "No purchases yet."}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="marketplace-grid">
                    {listings.map((l) => (
                        <ListingCard key={l.id} listing={l} onOpen={setSelected} />
                    ))}
                </div>
            )}
        </div>
    );
}
